import React, { useEffect, useState, useContext } from 'react';
import FormControl from 'components/Core/FormControl';
import SweetScrollContext from 'utils/SweetScrollContext';
import useHash from 'hooks/useHash';
import ToggleSwitch from 'components/Core/ToggleSwitch';
import RichText from 'components/RichText';
import useBreakpointView from 'utils/useBreakpointView';
import ToggleContent from './ToggleContent';
import { Container } from './styles';
import Select from '../../Core/Select';

const ContentfulToggle = ({
  anchorId,
  backgroundColor,
  belowToggleCopy1,
  belowToggleCopy2,
  defaultValue,
  titles,
  type,
  heading,
  subheading,
  startingIndex = 0,
  items,
  theme,
  displayCopyAboveToggle,
  overflowBackground,
  hideClass = 'hide',
  showClass = 'show',
  ...otherProps
}) => {
  const initialIndex = startingIndex;
  const [indexSelected, toggleIndexSelected] = useState(initialIndex);
  const [hash, updateHash] = useHash();
  const isMobile = useBreakpointView(['xs', 'sm']);
  const { scrollToAnchor } = useContext(SweetScrollContext);
  const isCommercialStyle = items?.every(
    (item) => item.sectionStyle === 'Commercial Section Style',
  );

  const handleClick = (value) => {
    toggleIndexSelected(value !== undefined ? value : indexSelected === 0 ? 1 : 0);
  };

  useEffect(() => {
    if (hash) {
      let activeIndex;
      let contentfulId;

      const toggleByHash = () => {
        const hashTitle = hash?.split('-')?.[0]?.substring(1);
        activeIndex = titles
          ?.map((title) => title.toLowerCase().replace(/\s+/g, ''))
          ?.indexOf(hashTitle);

        if (activeIndex !== null && activeIndex > -1) {
          handleClick(activeIndex);
        }
      };
      const isInPageFeatures = items.every(
        (item) => item.templateName === 'Chisel' || item.__typename === 'ContentfulInPageFeatures',
      );
      const isSections = items.every((item) => item.__typename === 'ContentfulSection');

      if (isInPageFeatures) {
        toggleByHash();
        requestAnimationFrame(() => {
          scrollToAnchor(`#section-${anchorId}`, {
            updateURL: false,
            offset: isMobile ? 100 : 0,
          });
        });
      }

      if (isSections) {
        const anchorMenuLink = hash?.indexOf('&') > -1;
        const hashToggle = hash.match(/-/g)?.length === 1;

        if (anchorMenuLink) {
          const activeTitle = titles?.[indexSelected];
          const titleIndex = hash?.indexOf(`${activeTitle?.toLowerCase()}-`);
          const idStartIndex = titleIndex + activeTitle.length + 1;
          contentfulId = hash?.substring(idStartIndex, idStartIndex + 22);
        } else if (hashToggle) {
          toggleByHash();
          contentfulId = hash?.split('-')?.[1];
        }

        setTimeout(
          () => {
            scrollToAnchor(`#section-${contentfulId}`, {
              updateURL: false,
            });
          },
          anchorMenuLink ? 0 : 900,
        );
      }
    }
  }, [hash]);

  const handleToggle = (value) => {
    if (titles.includes('Commercial')) {
      localStorage.setItem('selectedToggle', value);
      updateHash('');
    }
    handleClick(value);
    if (value !== undefined) {
      otherProps.handleDataLayer?.(titles?.[value]);
    }
  };

  return (
    <Container
      backgroundColor={backgroundColor}
      className={`toggle ${isCommercialStyle ? 'commercial' : ''}`}
      overflowBackground={overflowBackground}
      theme={theme}
      twoItemToggle={items.length === 2}
      displayCopyAboveToggle={displayCopyAboveToggle}
    >
      {heading && <h2>{heading}</h2>}
      {subheading && <h4>{subheading}</h4>}
      <div className={`flex ${displayCopyAboveToggle ? 'flex-col' : 'flex-col-reverse'}`}>
        {((belowToggleCopy1 && indexSelected === 0) ||
          (belowToggleCopy2 && indexSelected === 1)) && (
          <div className="toggle-copy">
            <RichText
              content={
                belowToggleCopy1 && indexSelected === 0 ? belowToggleCopy1 : belowToggleCopy2
              }
            />
          </div>
        )}
        {type === 'dropdown' ? (
          <FormControl sx={{ display: 'block', width: { md: 500, sm: 'auto' }, margin: '0 auto' }}>
            <Select
              handleChange={(id, value) => handleClick(titles.indexOf(value))}
              defaultValue={defaultValue}
              value={titles[indexSelected]}
              options={titles.map((x) => {
                return { value: x, label: x };
              })}
            />
          </FormControl>
        ) : (
          <ToggleSwitch
            checkedState={indexSelected === 1}
            handleClick={(value) => handleToggle(value)}
            indexSelected={indexSelected}
            titles={titles}
            theme={theme}
          />
        )}
      </div>

      <div className="toggle--content-container">
        {items.map((item, index) => (
          <div
            className={indexSelected === index ? showClass : hideClass}
            key={`toggle-content--${index}`}
          >
            <ToggleContent item={item} />
          </div>
        ))}
      </div>
    </Container>
  );
};

export default ContentfulToggle;
